import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { Container, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import BetButton from "../Buttons/BetButton";
import Scrollbar from "../Menu/Scrollbar";
import { formatToCustomLocale } from "../../../../utilities";

/**
 * ViewportMask - Creates a rectangular mask to clip scrollable content
 */
const ViewportMask = React.forwardRef(({ width, height }, ref) => (
  <Graphics
    ref={ref}
    name="viewport_mask"
    draw={(g) => {
      g.clear();
      g.beginFill(0x000000, 1);
      g.drawRect(0, 0, width, height);
      g.endFill();
    }}
  />
));

/**
 * PopiBetSelectContent - Scrollable bet selection grid component
 *
 * Handles:
 * - Grid layout of bet buttons (configurable columns)
 * - Mouse wheel and touch scrolling
 * - Button positioning (left-aligned for tablet landscape, centered otherwise)
 * - Viewport masking to clip content outside visible area
 * - Scrollbar rendering when content exceeds viewport
 * - Viewport culling to only render visible buttons for performance
 */
const BetSelectContent = ({
  bets,
  selectedBet,
  balance,
  currency,
  onBetSelect,
  viewportConfig,
  gridColumns,
  betButtonConfig,
  buttonSpacing,
  rowSpacing,
  leftAlign = false,
}) => {
  const scrollContainerRef = useRef(null);
  const viewportMaskRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // Touch scrolling state
  const touchStartY = useRef(0);
  const lastTouchY = useRef(0);
  const isDragging = useRef(false);

  /**
   * Button positioning calculation
   *
   * Vertical padding: Half button height + 10px to ensure buttons are fully visible
   * when scrolling (buttons use anchor 0.5, so center point is at position)
   *
   * Horizontal positioning:
   * - leftAlign = true (tablet landscape): Buttons start from left edge, aligned with title
   * - leftAlign = false (fullscreen): Buttons centered in viewport
   *
   * Grid layout: Calculates row/column from index, then positions accordingly
   */
  const verticalPadding = betButtonConfig.height / 2 + 10;

  const buttonPositions = useMemo(() => {
    return bets.map((betValue, index) => {
      const row = Math.floor(index / gridColumns);
      const col = index % gridColumns;

      let x;
      if (leftAlign) {
        // Left-aligned: position includes button width + spacing
        x =
          betButtonConfig.width / 2 +
          col * (betButtonConfig.width + buttonSpacing);
      } else {
        // Centered: calculate total grid width and center it
        const totalWidth =
          (gridColumns - 1) * (betButtonConfig.width + buttonSpacing);
        const startX = -totalWidth / 2;
        x = startX + col * (betButtonConfig.width + buttonSpacing);
      }

      const y = verticalPadding + row * (betButtonConfig.height + rowSpacing);

      return { x, y };
    });
  }, [
    bets,
    gridColumns,
    buttonSpacing,
    rowSpacing,
    verticalPadding,
    leftAlign,
    betButtonConfig.width,
    betButtonConfig.height,
  ]);

  // Calculate scroll limits (include padding in total height)
  const rows = Math.ceil(bets.length / gridColumns);
  const totalContentHeight =
    verticalPadding * 2 + rows * (betButtonConfig.height + rowSpacing);
  const maxScrollY = Math.max(
    0,
    totalContentHeight - viewportConfig.viewportHeight
  );

  // Wheel scroll handler
  const handleScroll = useCallback(
    (event) => {
      const scrollSpeed = 20;
      const newScrollY =
        scrollY + (event.deltaY > 0 ? scrollSpeed : -scrollSpeed);
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));

      if (clampedScrollY !== scrollY) {
        event.preventDefault();
        event.stopPropagation();
        setScrollY(clampedScrollY);
      }
    },
    [scrollY, maxScrollY]
  );

  // Touch scroll handlers
  const handlePointerDown = useCallback((event) => {
    if (event.data.pointerType !== "touch" && event.data.pointerType !== "pen")
      return;
    touchStartY.current = event.data.global.y;
    lastTouchY.current = event.data.global.y;
    isDragging.current = true;
  }, []);

  const handlePointerMove = useCallback(
    (event) => {
      if (!isDragging.current) return;
      if (
        event.data.pointerType !== "touch" &&
        event.data.pointerType !== "pen"
      )
        return;

      event.stopPropagation();
      const currentY = event.data.global.y;
      const deltaY = (lastTouchY.current - currentY) * 2.0;
      lastTouchY.current = currentY;

      const newScrollY = scrollY + deltaY;
      const clampedScrollY = Math.max(0, Math.min(newScrollY, maxScrollY));
      setScrollY(clampedScrollY);
    },
    [scrollY, maxScrollY]
  );

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Apply viewport mask to clip content outside scrollable area
  useEffect(() => {
    if (viewportMaskRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.mask = viewportMaskRef.current;
    }
  }, []);

  /**
   * Scrollbar positioning and sizing
   *
   * Position: Right edge of viewport minus offset for visual spacing
   * Height: Matches the actual visible content area (viewport height)
   *
   * The scrollbar height represents the visible viewport area that shows the buttons.
   */
  const scrollbarOffset = viewportConfig.scrollbarOffset || 10;
  const scrollbarX = viewportConfig.maxWidth - scrollbarOffset;
  const scrollbarPadding = 0; // No additional padding needed
  const scrollbarHeight = viewportConfig.viewportHeight; // Full viewport height

  return (
    <Container
      name="bet_select_scrollable_content"
      onwheel={handleScroll}
      onpointerdown={handlePointerDown}
      onpointermove={handlePointerMove}
      onpointerup={handlePointerUp}
      onpointercancel={handlePointerUp}
      onpointerleave={handlePointerUp}
      eventMode="static"
      hitArea={
        new PIXI.Rectangle(
          0,
          0,
          viewportConfig.maxWidth,
          viewportConfig.viewportHeight
        )
      }
    >
      {/* Viewport Mask - clips content outside scrollable area */}
      <ViewportMask
        ref={viewportMaskRef}
        width={viewportConfig.maxWidth}
        height={viewportConfig.viewportHeight}
      />

      {/* Scrollbar */}
      {maxScrollY > 0 && (
        <Scrollbar
          scrollY={scrollY}
          maxScrollY={maxScrollY}
          viewportHeight={scrollbarHeight}
          x={scrollbarX}
          y={scrollbarPadding}
        />
      )}

      {/* Bet Buttons Container - left-aligned or centered based on prop */}
      <Container
        ref={scrollContainerRef}
        name="bet_buttons_container"
        x={leftAlign ? 0 : viewportConfig.maxWidth / 2}
        y={-scrollY}
      >
        {bets.map((betValue, index) => {
          const position = buttonPositions[index];
          const isSelected = betValue === selectedBet;
          const isDisabled = betValue > balance;

          // Viewport culling - only render visible buttons
          const isVisible =
            position.y - scrollY > -betButtonConfig.height &&
            position.y - scrollY <
              viewportConfig.viewportHeight + betButtonConfig.height;

          if (!isVisible) return null;

          return (
            <BetButton
              key={index}
              x={position.x}
              y={position.y}
              text={`${formatToCustomLocale(
                betValue
              )} ${currency}`}
              onClick={() => onBetSelect(betValue)}
              disabled={isDisabled}
              active={isSelected}
              width={betButtonConfig.width}
              height={betButtonConfig.height}
              fontSize={betButtonConfig.fontSize}
            />
          );
        })}
      </Container>
    </Container>
  );
};

export default BetSelectContent;
